import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STORAGE_KEY = 'grouth_admin_token';

const adminAxios = axios.create({ baseURL: API, withCredentials: true });

adminAxios.interceptors.request.use((config) => {
  const token = localStorage.getItem(STORAGE_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

adminAxios.interceptors.response.use(
  (res) => res,
  (err) => {
    // Auto-logout on 401 from any admin endpoint
    if (err?.response?.status === 401) {
      localStorage.removeItem(STORAGE_KEY);
      // Avoid hard redirect from API layer; consumer can handle
    }
    return Promise.reject(err);
  }
);

export const tokenStore = {
  get: () => localStorage.getItem(STORAGE_KEY),
  set: (t) => localStorage.setItem(STORAGE_KEY, t),
  clear: () => localStorage.removeItem(STORAGE_KEY),
};

export const adminApi = {
  // Auth
  login: (email, password) =>
    adminAxios.post('/auth/login', { email, password }).then((r) => r.data),
  verify2fa: (challenge_token, code, trust_device = false) =>
    adminAxios
      .post('/auth/2fa/verify', { challenge_token, code, trust_device })
      .then((r) => r.data),
  me: () => adminAxios.get('/auth/me').then((r) => r.data),
  logout: () => adminAxios.post('/auth/logout').then((r) => r.data),

  // 2FA self-service
  twofaStatus: () => adminAxios.get('/admin/2fa/status').then((r) => r.data),
  twofaDisable: (current_password, code) =>
    adminAxios.post('/admin/2fa/disable', { current_password, code }).then((r) => r.data),

  // Trusted devices (cookie must be sent to /api/auth/... so withCredentials is on)
  listTrustedDevices: () => adminAxios.get('/auth/trusted-devices').then((r) => r.data),
  revokeTrustedDevice: (id) => adminAxios.delete(`/auth/trusted-devices/${id}`).then((r) => r.data),
  revokeAllTrustedDevices: () => adminAxios.delete('/auth/trusted-devices').then((r) => r.data),

  // Stats
  stats: () => adminAxios.get('/admin/stats').then((r) => r.data),

  // CRM: contacts
  listContacts: () => adminAxios.get('/admin/contacts').then((r) => r.data),
  getContact: (id) => adminAxios.get(`/admin/contacts/${id}`).then((r) => r.data),
  updateContact: (id, payload) =>
    adminAxios.patch(`/admin/contacts/${id}`, payload).then((r) => r.data),
  deleteContact: (id) => adminAxios.delete(`/admin/contacts/${id}`).then((r) => r.data),

  // Subscribers
  listSubscribers: () => adminAxios.get('/admin/subscribers').then((r) => r.data),
  deleteSubscriber: (id) => adminAxios.delete(`/admin/subscribers/${id}`).then((r) => r.data),

  // Articles
  listArticles: () => adminAxios.get('/admin/articles').then((r) => r.data),
  createArticle: (payload) => adminAxios.post('/admin/articles', payload).then((r) => r.data),
  updateArticle: (slug, payload) =>
    adminAxios.put(`/admin/articles/${slug}`, payload).then((r) => r.data),
  deleteArticle: (slug) => adminAxios.delete(`/admin/articles/${slug}`).then((r) => r.data),

  // Case studies (admin)
  listCaseStudies: () => adminAxios.get('/admin/case-studies').then((r) => r.data),
  createCaseStudy: (payload) => adminAxios.post('/admin/case-studies', payload).then((r) => r.data),
  updateCaseStudy: (id, payload) =>
    adminAxios.put(`/admin/case-studies/${id}`, payload).then((r) => r.data),
  deleteCaseStudy: (id) => adminAxios.delete(`/admin/case-studies/${id}`).then((r) => r.data),

  // Settings
  changePassword: (current_password, new_password) =>
    adminAxios.post('/admin/change-password', { current_password, new_password }).then((r) => r.data),

  // CRM extras
  bulkContacts: (payload) => adminAxios.post('/admin/contacts/bulk', payload).then((r) => r.data),
  exportContactsCsv: () => adminAxios.get('/admin/contacts.csv', { responseType: 'blob' }).then((r) => r.data),
  addContactNote: (id, text) =>
    adminAxios.post(`/admin/contacts/${id}/notes`, { text }).then((r) => r.data),
  deleteContactNote: (id, noteId) =>
    adminAxios.delete(`/admin/contacts/${id}/notes/${noteId}`).then((r) => r.data),

  // Analytics
  analytics: (days = 30) => adminAxios.get(`/admin/analytics?days=${days}`).then((r) => r.data),

  // Audit log
  auditLog: (limit = 100) => adminAxios.get(`/admin/audit-log?limit=${limit}`).then((r) => r.data),

  // Global search
  search: (q) => adminAxios.get(`/admin/search?q=${encodeURIComponent(q)}`).then((r) => r.data),

  // In-app notifications
  notifications: () => adminAxios.get('/admin/notifications').then((r) => r.data),
  markNotificationsRead: () => adminAxios.post('/admin/notifications/mark-read').then((r) => r.data),

  // Per-recipient broadcast logs
  broadcastLogs: (broadcastId) =>
    adminAxios.get(`/admin/broadcast/${broadcastId}/logs`).then((r) => r.data),

  // Newsletter broadcast
  broadcastStatus: () => adminAxios.get('/admin/broadcast/status').then((r) => r.data),
  broadcastSend: (payload, idempotencyKey) =>
    adminAxios
      .post('/admin/broadcast/send', payload, idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : undefined)
      .then((r) => r.data),
  broadcastHistory: () => adminAxios.get('/admin/broadcast/history').then((r) => r.data),
  broadcastTest: () => adminAxios.post('/admin/broadcast/test').then((r) => r.data),

  // Weekly digest
  digestStatus: () => adminAxios.get('/admin/digest/status').then((r) => r.data),
  digestPreview: (days = 7) => adminAxios.get(`/admin/digest/preview?days=${days}`).then((r) => r.data),
  digestSend: (days = 7) => adminAxios.post('/admin/digest/send', { days }).then((r) => r.data),
  digestHistory: () => adminAxios.get('/admin/digest/history').then((r) => r.data),

  // Reorder
  reorderCaseStudies: (ids) =>
    adminAxios.post('/admin/case-studies/reorder', { ids }).then((r) => r.data),

  // Uploads
  uploadImage: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return adminAxios
      .post('/admin/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then((r) => r.data);
  },

  // Broadcast publish toggle
  setBroadcastPublic: (id, isPublic) =>
    adminAxios
      .patch(`/admin/broadcast/${id}/publish`, { is_public: !!isPublic })
      .then((r) => r.data),

  // CSV import for contacts
  importContactsCsv: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return adminAxios
      .post('/admin/contacts/import-csv', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data);
  },

  // Team / user management (admin role only)
  listUsers: () => adminAxios.get('/admin/users').then((r) => r.data),
  createUser: (data) => adminAxios.post('/admin/users', data).then((r) => r.data),
  updateUser: (id, data) => adminAxios.patch(`/admin/users/${id}`, data).then((r) => r.data),
  resetUserPassword: (id, new_password) =>
    adminAxios.post(`/admin/users/${id}/reset-password`, { new_password }).then((r) => r.data),
  resetUser2fa: (id) =>
    adminAxios.post(`/admin/users/${id}/reset-2fa`).then((r) => r.data),
  deleteUser: (id) => adminAxios.delete(`/admin/users/${id}`).then((r) => r.data),
};

// FastAPI 422 validation returns an array of {msg,...} — convert to a string for UI
export const formatApiError = (detail) => {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
};
